/* eslint-disable @typescript-eslint/unbound-method */
import { Component, OnInit, Input } from '@angular/core';
import { BaseComponentDirective } from '@core/component/base.component';
import { HttpClient } from '@angular/common/http';
import { LeadListService } from '@app/lead/services/leadlist-service';
import { first, takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadViewModel } from '@app/lead/model/lead-view.model';
import { UserMedicationModel } from '../model/user-medication-model';
import { Subject, of, Subscription } from 'rxjs';
import { DialerContentService } from '@app/shared/services/dialer-content-service';

export interface MedicationData {
    medication: string;
    strength: string;
    dosage: string;
    route: string;
    frequency: string;
    notes: string;
    source: 'rxterms' | 'manual';
}

interface RxTermsResult {
    name: string;
    strengths: string[];
}

@Component({
    selector: 'app-lead-medications',
    templateUrl: './lead-medications.component.html',
    styleUrls: ['./lead-medications.component.scss'],
})
export class LeadMedicationsComponent extends BaseComponentDirective implements OnInit {
    @Input() leadId: string;

    entryMode: 'search' | 'manual' = 'search';
    searchTerm = '';
    searchResults: RxTermsResult[] = [];
    searching = false;
    showResults = false;
    selectedMedication: RxTermsResult = null;
    selectedStrength = '';
    manualMedicationName = '';
    manualStrength = '';
    dosage = '';
    selectedRoute = '';
    selectedFrequency = '';
    medicationNotes = '';

    editMode = false;
    editId: string = null;

    leadMedications: any[] = [];
    loadingData = false;
    showLoader = false;
    submitted = false;

    private searchSubject = new Subject<string>();
    dialerSubscribe: Subscription;

    routeOptions = [
        { label: 'Oral', value: 'Oral' },
        { label: 'Topical', value: 'Topical' },
        { label: 'Inhalation', value: 'Inhalation' },
        { label: 'Injection', value: 'Injection' },
        { label: 'Sublingual', value: 'Sublingual' },
        { label: 'Transdermal', value: 'Transdermal' },
        { label: 'Rectal', value: 'Rectal' },
        { label: 'Ophthalmic', value: 'Ophthalmic' },
        { label: 'Otic', value: 'Otic' },
        { label: 'Nasal', value: 'Nasal' },
        { label: 'Vaginal', value: 'Vaginal' },
        { label: 'IV', value: 'IV' },
        { label: 'Other', value: 'Other' },
    ];

    frequencyOptions = [
        { label: 'Once daily', value: 'Once daily' },
        { label: 'Twice daily', value: 'Twice daily' },
        { label: 'Three times daily', value: 'Three times daily' },
        { label: 'Four times daily', value: 'Four times daily' },
        { label: 'Every morning', value: 'Every morning' },
        { label: 'Every evening', value: 'Every evening' },
        { label: 'At bedtime', value: 'At bedtime' },
        { label: 'Every 4 hours', value: 'Every 4 hours' },
        { label: 'Every 6 hours', value: 'Every 6 hours' },
        { label: 'Every 8 hours', value: 'Every 8 hours' },
        { label: 'Every 12 hours', value: 'Every 12 hours' },
        { label: 'Weekly', value: 'Weekly' },
        { label: 'As needed (PRN)', value: 'As needed (PRN)' },
        { label: 'Other', value: 'Other' },
    ];

    private readonly NIH_API_URL = 'https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search';

    constructor(
        private leadService: LeadListService,
        private http: HttpClient,
        private confirmationService: ConfirmationService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private dialerContentService: DialerContentService,
    ) {
        super();
        this.dialerSubscribe = this.dialerContentService.getUpdate().pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            this.leadId = message.text;
            this.getLeadMedicationsList();
        });
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.getLeadMedicationsList();
        this.setupSearch();
    }

    private setupSearch(): void {
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.onDestroy$),
                switchMap((term) => {
                    if (!term || term.length < 2) {
                        return of(null);
                    }
                    this.searching = true;
                    return this.http.get<any[]>(
                        `${this.NIH_API_URL}?terms=${encodeURIComponent(term)}&ef=STRENGTHS_AND_FORMS`,
                    );
                }),
            )
            .subscribe({
                next: (response) => {
                    this.searching = false;
                    if (!response) {
                        this.searchResults = [];
                        this.showResults = false;
                        return;
                    }
                    const names: string[] = response[1] || [];
                    const extras = response[2] || {};
                    const strengthsAndForms: string[][] = extras.STRENGTHS_AND_FORMS || [];

                    this.searchResults = names.map((name, i) => ({
                        name,
                        strengths: strengthsAndForms[i] || [],
                    }));
                    this.showResults = this.searchResults.length > 0;
                },
                error: () => {
                    this.searching = false;
                    this.searchResults = [];
                },
            });
    }

    onSearchInput(): void {
        this.searchSubject.next(this.searchTerm);
        this.selectedMedication = null;
        this.selectedStrength = '';
    }

    selectMedication(result: RxTermsResult): void {
        this.selectedMedication = result;
        this.searchTerm = result.name;
        this.showResults = false;
        this.selectedStrength = '';
    }

    selectStrength(strength: string): void {
        this.selectedStrength = strength;
    }

    hideResults(): void {
        setTimeout(() => (this.showResults = false), 200);
    }

    isFormValid(): boolean {
        if (this.entryMode === 'search') {
            return !!this.selectedMedication;
        }
        return !!this.manualMedicationName.trim();
    }

    onSubmit(): void {
        this.submitted = true;
        if (!this.isFormValid()) return;

        this.loading = true;
        const medName = this.entryMode === 'search' ? this.selectedMedication?.name || '' : this.manualMedicationName;
        const strength = this.entryMode === 'search' ? this.selectedStrength : this.manualStrength;

        const data: MedicationData = {
            medication: medName,
            strength,
            dosage: this.dosage,
            route: this.selectedRoute,
            frequency: this.selectedFrequency,
            notes: this.medicationNotes,
            source: this.entryMode === 'search' ? 'rxterms' : 'manual',
        };
        const value = JSON.stringify(data);

        if (this.editMode && this.editId) {
            const userId = this.resolveUserId();
            this.leadService
                .editLeadMedication(
                    userId,
                    new UserMedicationModel({
                        id: this.editId,
                        value,
                        userId,
                        statusId: 1,
                    }),
                )
                .pipe(first(), takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.loading = false;
                        this.resetForm();
                        this.getLeadMedicationsList();
                        this.messageService.add({
                            key: 'tst',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Medication updated successfully',
                        });
                    },
                    error: () => {
                        this.loading = false;
                        this.messageService.add({
                            key: 'tst',
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Something went wrong, please try again',
                        });
                    },
                });
        } else {
            this.leadService
                .addLeadMedication(this.leadId, {
                    userId: this.leadId,
                    value,
                    statusId: 1,
                })
                .pipe(first(), takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.loading = false;
                        this.resetForm();
                        this.getLeadMedicationsList();
                        this.messageService.add({
                            key: 'tst',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Medication added successfully',
                        });
                    },
                    error: () => {
                        this.loading = false;
                    },
                });
        }
    }

    resetForm(): void {
        this.searchTerm = '';
        this.searchResults = [];
        this.selectedMedication = null;
        this.selectedStrength = '';
        this.manualMedicationName = '';
        this.manualStrength = '';
        this.dosage = '';
        this.selectedRoute = '';
        this.selectedFrequency = '';
        this.medicationNotes = '';
        this.showResults = false;
        this.editMode = false;
        this.editId = null;
        this.submitted = false;
    }

    getLeadMedicationsList(): void {
        this.loadingData = true;
        this.leadService
            .getLeadMedication(this.leadId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
                this.loadingData = false;
                this.leadMedications = result;
            });
    }

    parseMedication(value: string): MedicationData | null {
        if (!value) return null;
        try {
            const parsed = JSON.parse(value);
            if (parsed && parsed.medication) return parsed;
            return null;
        } catch {
            return null;
        }
    }

    editMedication(med: any): void {
        const data = this.parseMedication(med.value);
        if (data) {
            this.editMode = true;
            this.editId = med.id;
            this.entryMode = data.source === 'rxterms' ? 'search' : 'manual';
            if (data.source === 'rxterms') {
                this.searchTerm = data.medication;
                this.selectedMedication = { name: data.medication, strengths: data.strength ? [data.strength] : [] };
                this.selectedStrength = data.strength;
            } else {
                this.manualMedicationName = data.medication;
                this.manualStrength = data.strength;
            }
            this.dosage = data.dosage || '';
            this.selectedRoute = data.route || '';
            this.selectedFrequency = data.frequency || '';
            this.medicationNotes = data.notes || '';
        } else {
            // Legacy HTML — strip tags, load into manual mode
            this.editMode = true;
            this.editId = med.id;
            this.entryMode = 'manual';
            const tmp = document.createElement('div');
            tmp.innerHTML = med.value;
            this.manualMedicationName = tmp.textContent || tmp.innerText || med.value;
            this.manualStrength = '';
            this.dosage = '';
            this.selectedRoute = '';
            this.selectedFrequency = '';
            this.medicationNotes = '';
        }
    }

    deleteUserMedication(id: string): void {
        const userId = this.resolveUserId();
        this.confirmationService.confirm({
            message: 'Are you sure that you want to delete this medication?',
            header: 'Confirmation',
            key: 'leadMedicationDelete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.leadService
                    .deleteLeadMedication(userId, id)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                        next: () => {
                            this.getLeadMedicationsList();
                            this.messageService.add({
                                key: 'tst',
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Successfully deleted medication',
                            });
                        },
                        error: (error) => {
                            this.messageService.add({
                                key: 'tst',
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Error in deleting medication',
                            });
                            this.logger.log('error user medications', error);
                        },
                    });
            },
        });
    }

    cancelEdit(): void {
        this.resetForm();
    }

    private resolveUserId(): string {
        const getLatestLead = JSON.parse(localStorage.getItem('Launch_Dialer')) as LeadViewModel[];
        const getParams = this.activatedRoute.snapshot.params;
        if (getParams.hasOwnProperty('id')) {
            return getParams.id;
        } else if (getLatestLead) {
            return getLatestLead[0].id;
        }
        return this.leadId;
    }
}
